import { reSignUpApiCall } from '@guryou/api-client-gql';
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  updatePassword,
} from 'aws-amplify/auth';

import { ActionStatus, basicReducer, buildActionType, NotificationType, SuccessNotification } from '../core';
import { CURRENT_USER } from '../new';
import {
  CHANGE_PASSWORD,
  CHECK_AUTH,
  FORGOT_PASSWORD,
  LOGIN,
  LOGOUT,
  NOTIFICATION,
  OWNER,
  RESEND_SIGN_UP,
  RESET_PASSWORD,
  SET_PASSOWRD,
  SIGN_UP,
  STORAGE_PERSIST,
} from '../types';

import { resetState } from './resetActions';

const NEW_PASSWORD_REQUIRED = 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED';

// TEMP
const SUCCESS_SIGN_UP = 'Iscrizione riuscita';
const PLEASE_CHECK_EMAIL = 'Si prega di controllare la tua email';

const cognitoLogin =
  ({
    email,
    password,
    persistor,
    federatedProvider = null,
    appName = process.env.REACT_APP_NAME_DEAL,
    appVersion = process.env.REACT_APP_VERSION,
    appOs = process.env.REACT_APP_APP_OS,
  }) =>
  async dispatch => {
    dispatch(startDispatch(LOGIN));

    try {
      if (federatedProvider) {
        await signInWithRedirect({ provider: federatedProvider });
        return;
      }

      const signInInput = {
        username: email,
        password,
        options: {
          clientMetadata: {
            appName,
            appVersion,
            appOs,
          },
          userAttributes: {
            appName,
            appVersion,
            appOs,
          },
        },
      };

      let result;
      try {
        result = await signIn(signInInput);
      } catch (e) {
        if (e?.name === 'UserAlreadyAuthenticatedException' || e?.message?.includes('already a signed in user')) {
          await signOut();
          result = await signIn(signInInput);
        } else {
          throw e;
        }
      }

      if (result.challengeName === 'NEW_PASSWORD_REQUIRED' || result.challengeName === NEW_PASSWORD_REQUIRED) {
        return 'NEW_PASSWORD_REQUIRED';
      }

      switch (result.nextStep?.signInStep) {
        case 'DONE': {
          const { tokens } = await fetchAuthSession();
          const payload = tokens?.idToken?.payload;
          const jwtToken = tokens?.idToken?.toString();
          dispatch(successDispatch(LOGIN, { ...payload, jwtToken }, SuccessNotification.SUCCESS));

          if (persistor) {
            await persistor.flush();
          }
          dispatch(successDispatch(STORAGE_PERSIST));
          break;
        }

        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED': {
          // user must set a new password
          return NEW_PASSWORD_REQUIRED;
        }

        default:
          // handle other challenges like MFA, SMS, etc.
          //eslint-disable-next-line
          console.warn('Unhandled challenge:', result.nextStep?.signInStep);
          break;
      }
    } catch (e) {
      dispatch(errorDispatch(e, LOGIN));
    }
  };

const cognitoForgotPassword = email => async dispatch => {
  dispatch(startDispatch(FORGOT_PASSWORD));
  try {
    const responseData = await resetPassword({ username: email });
    dispatch(successDispatch(FORGOT_PASSWORD, responseData, SuccessNotification.SUCCESS));
  } catch (e) {
    dispatch(errorDispatch(e, FORGOT_PASSWORD));
  }
};

const cognitoSetNewPassword = data => async dispatch => {
  dispatch(startDispatch(RESET_PASSWORD));
  const { email, code, newPassword } = data;
  try {
    const responseData = await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
    dispatch(successDispatch(RESET_PASSWORD, responseData, SuccessNotification._UPDATE));
  } catch (e) {
    dispatch(errorDispatch(e, RESET_PASSWORD));
  }
};

// `activate` reaches the Cognito pre-authentication lambda as `validationData.activate`, which sets users.active = true.
// Lost in the Amplify v3 -> v6 upgrade (b9e186e30), where v3's 3rd arg became `options.clientMetadata`.
const cognitoVerifyUser = async (email, code) => signIn({ username: email, password: code, options: { clientMetadata: { activate: 'true' } } });

const cognitoFirstLoginSetPassword =
  ({ newPassword }, user, persistor) =>
  async dispatch => {
    dispatch(startDispatch(SET_PASSOWRD));
    try {
      if (user?.nextStep?.signInStep !== 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        // invalid/expired/already-used link: fail explicitly instead of leaving the state loading forever
        dispatch(errorDispatch(new Error('Invalid or expired verification link'), SET_PASSOWRD));
        return;
      }

      await confirmSignIn({ challengeResponse: newPassword });
      const { tokens } = await fetchAuthSession();
      const payload = tokens?.idToken?.payload;
      const jwtToken = tokens?.idToken?.toString();
      dispatch(successDispatch(SET_PASSOWRD, { ...payload, jwtToken }, SuccessNotification.SUCCESS));

      //TODO: check if ok for other apps
      dispatch(successDispatch(LOGIN, { ...payload, jwtToken }, SuccessNotification.SUCCESS));
      if (persistor) {
        await persistor.flush();
      }
    } catch (e) {
      dispatch(errorDispatch(e, SET_PASSOWRD));
    }
  };

const cognitoSignUp = (username, password) => async dispatch => {
  dispatch(startDispatch(SIGN_UP));
  try {
    const user = await signUp({ username, password });
    dispatch(successDispatch(SIGN_UP, user, SUCCESS_SIGN_UP));
    dispatch({ type: NOTIFICATION, payload: { message: PLEASE_CHECK_EMAIL, variant: NotificationType.Default } });
  } catch (e) {
    dispatch(errorDispatch(e, SIGN_UP));
  }
};

const cognitoResendSignUp = email => async dispatch => {
  dispatch(startDispatch(RESEND_SIGN_UP));
  try {
    // const { user } = await Auth.resendSignUp(email);
    const resend = await reSignUpApiCall({ email });
    dispatch(successDispatch(RESEND_SIGN_UP, resend, SUCCESS_SIGN_UP));
    dispatch({ type: NOTIFICATION, payload: { message: PLEASE_CHECK_EMAIL, variant: NotificationType.Default } });
  } catch (e) {
    dispatch(errorDispatch(e, RESEND_SIGN_UP));
  }
};

const cognitoChangeUserPassword = (oldPassword, newPassword) => async dispatch => {
  dispatch(startDispatch(CHANGE_PASSWORD));
  try {
    const user = await getCurrentUser();
    await updatePassword({ user, oldPassword, newPassword });
    dispatch(successDispatch(CHANGE_PASSWORD, {}, SuccessNotification.SUCCESS));
  } catch (e) {
    dispatch(errorDispatch(e, CHANGE_PASSWORD));
  }
};

const cognitoLogout = () => async dispatch => {
  try {
    dispatch(resetState(CURRENT_USER));
    dispatch(resetState(OWNER));
    dispatch({ type: LOGOUT });
    await signOut();
  } catch (e) {
    new Error(e);
  }
};

const checkAuth = () => async dispatch => {
  dispatch(startDispatch(CHECK_AUTH));
  try {
    await getCurrentUser();
    dispatch(successDispatch(CHECK_AUTH));
  } catch (e) {
    dispatch(errorDispatch(e, CHECK_AUTH));
  }
};

const resetForgotPassState = () => async dispatch => dispatch(resetState(FORGOT_PASSWORD));
const resetLoginState = () => async dispatch => dispatch(resetState(LOGIN));
const resetCheckAuthState = () => async dispatch => dispatch(resetState(CHECK_AUTH));

export {
  checkAuth,
  cognitoChangeUserPassword,
  cognitoFirstLoginSetPassword,
  cognitoForgotPassword,
  cognitoLogin,
  cognitoLogout,
  cognitoResendSignUp,
  cognitoSetNewPassword,
  cognitoSignUp,
  cognitoVerifyUser,
  resetCheckAuthState,
  resetForgotPassState,
  resetLoginState,
};

const startDispatch = action => dispatch => dispatch({ type: buildActionType(action, ActionStatus.START) });

const errorDispatch = (error, action) => dispatch => {
  dispatch({ type: buildActionType(action, ActionStatus.FAILED), payload: error });
  dispatch({ type: NOTIFICATION, payload: { message: error.message, variant: NotificationType.Error } });
};

const successDispatch =
  (action, payload = {}, message) =>
  dispatch => {
    dispatch({ type: buildActionType(action, ActionStatus.DONE), payload });
    message && dispatch({ type: NOTIFICATION, payload: { message, variant: NotificationType.Success } });
  };

export const cognitoReducers = {
  forgotPassword: basicReducer(FORGOT_PASSWORD),
  setNewPassword: basicReducer(RESET_PASSWORD),
  firstLogin: basicReducer(SET_PASSOWRD),
  signUp: basicReducer(SIGN_UP),
  changePassword: basicReducer(CHANGE_PASSWORD),
  resendSignUp: basicReducer(RESEND_SIGN_UP),
};
